import React from 'react';
import { Leaf, Droplets, Zap, TreePine, Recycle } from 'lucide-react';
import { useMissionImpacts } from '../hooks/useMissionImpacts';

interface MissionImpactBadgeProps {
  missionId: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export default function MissionImpactBadge({ 
  missionId, 
  size = 'md',
  showLabel = true
}: MissionImpactBadgeProps) {
  const { missionImpacts, loading, getMissionImpacts } = useMissionImpacts();
  
  const impacts = getMissionImpacts(missionId);
  
  if (loading || impacts.length === 0) {
    return null;
  }
  
  // Get icon and color based on impact type
  const getImpactIcon = (impactType: string) => {
    switch (impactType) {
      case 'co2_saved':
        return <Leaf className={`${getIconSize(size)} text-green-600`} />;
      case 'waste_reduced':
        return <Recycle className={`${getIconSize(size)} text-orange-600`} />;
      case 'trees_planted':
        return <TreePine className={`${getIconSize(size)} text-emerald-600`} />;
      case 'water_saved':
        return <Droplets className={`${getIconSize(size)} text-blue-600`} />;
      case 'energy_saved':
        return <Zap className={`${getIconSize(size)} text-yellow-600`} />;
      default:
        return <Leaf className={`${getIconSize(size)} text-emerald-600`} />;
    }
  };
  
  const getIconSize = (size: string) => {
    switch (size) {
      case 'sm': return 'h-3 w-3';
      case 'lg': return 'h-5 w-5';
      default: return 'h-4 w-4';
    }
  };
  
  const getBadgeSize = (size: string) => {
    switch (size) {
      case 'sm': return 'px-1.5 py-0.5 text-xs';
      case 'lg': return 'px-3 py-1.5 text-sm';
      default: return 'px-2 py-1 text-xs';
    }
  };
  
  // Show only the first impact for simplicity
  const primaryImpact = impacts[0];
  
  return (
    <div className={`inline-flex items-center space-x-1 bg-emerald-100 text-emerald-800 rounded-full font-medium ${getBadgeSize(size)}`}>
      {getImpactIcon(primaryImpact.impact_type_name)}
      {showLabel && (
        <span>+{primaryImpact.quantity_per_completion} {primaryImpact.unit}</span>
      )}
      {impacts.length > 1 && showLabel && (
        <span className="text-emerald-600">+{impacts.length - 1}</span>
      )}
    </div>
  );
}